/*
 *   File : login.js
 *   Author : https://evoqins.com
 *   Description : Login page
 *   Integrations : js-cookie
 *   Version : 1.0.0
*/

import React, { useEffect, useRef, useState } from 'react';
import Cookies from "js-cookie";
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

//Custom components
import { CustomPhoneInput, OTPInput } from "../../Component/FormElements";
import { PrimaryButton } from "../../Component/Buttons";
import { Icon } from '../../Component/Icon';
import { APIService } from '../../Service';
import { _scrollToTop } from '../../Helper';

//Style
import style from "../../Styles/Component/auth.module.scss";

// Assets
import LoginStep1Banner from "../../Assets/Images/Auth/login-step.webp";
import LoginStep2Banner from "../../Assets/Images/Auth/otp-step.webp";

const Login = () => {

    const inputRef = useRef(null);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [step, setStep] = useState(1);

    const [number, setNumber] = useState("");
    const [numberError, setNumberError] = useState("");

    // Otp 
    const [otpValue, setOtpValue] = useState("");
    const [otpValueError, setOtpValueError] = useState("");
    const [focusedInput, setFocusedInput] = useState(2);
    const [loading, setLoading] = useState(false);
    const [disableResend, setDisabledResend] = useState(false);
    const [otpTimer, setOTPTimer] = useState(30);

    useEffect(() => {
        if (otpTimer !== 0 && step === 2) {
            setTimeout(() => {
                setOTPTimer(timer => timer - 1);
            }, 1000);
        }
    }, [otpTimer, step]);

    useEffect(() => {
        // Scroll the page to top
        _scrollToTop();
    }, [step]);

    useEffect(() => {
        setNumberError("");
    }, [number]);

    useEffect(() => {
        setOtpValueError("");
    }, [otpValue]);

    // handle navigate to signup
    const _handleSignUp = () => {
        navigate("/signup");
    };

    //handle Otp change
    const _handleChangeOTP = (value) => {
        setOtpValue(value);
    };

    // handle resend OTP 
    const _handleResendOtp = () => {
        setDisabledResend(true);
        // API Call
        _getLoginOTP("resend");
    };

    // handle back step 
    const _handleBack = () => {
        setStep(step - 1);
        setOtpValue("");
        setOtpValueError("");
        setOTPTimer(30);
    };

    // handle get otp
    const _handleGetOtp = () => {

        let validate_number = /^[6-9]\d{9}$/;

        if (number.trim().length === 0) {
            setNumberError("Enter your phone number");
        } else if (!validate_number.test(number)) {
            setNumberError("Enter a valid phone number");
        } else {
            // API call
            setLoading(true);
            _getLoginOTP();
        }
    };

    // handle verify OTP 
    const _handleVerifyOtp = () => {
        if (otpValue.length < 4) {
            setOtpValueError("Please provide received otp");
            setFocusedInput(2);
        } else {
            // API call
            setLoading(true);
            _verifyOTP();
        }
    };

    // ===================================== //
    // ============ API CALLS ============== //

    // Send Signup OTP to mobile
    const _getLoginOTP = async (type) => {
        let url = "auth/login";
        let data = {
            "username": number,
            "source": "WEB"
        };

        const response = await APIService(url, data);

        if (response.status_code === 200) {
            if (type == "resend") {
                toast.dismiss();
                toast.success('OTP re-sent successfully');
            }
            setOTPTimer(30);
            setStep(2);
            setOtpValue("");
            setFocusedInput(2);
        } else {

            if (response.message === "User not found.") {
                toast.dismiss();
                toast.error("Please create an account to login");
                navigate("/signup", {
                    state: {
                        mobile: number
                    }
                });
            } else {
                toast.dismiss();
                toast.error(response.message);
            }
        }

        setLoading(false);
        setDisabledResend(false);
    };

    // Verify login OTP
    const _verifyOTP = async () => {
        let url = "auth/validate-login-otp";
        let data = {
            "username": number,
            "otp": otpValue,
            "source": "WEB"
        };

        const response = await APIService(url, data);

        if (response.status_code === 200) {
            let options = { domain: process.env.REACT_APP_DOMAIN, path: '/', expires: 15 };
            let access_token = response.data.token_info.access_token;
            dispatch({ type: "UPDATE_ACCESS_TOKEN", payload: access_token });
            dispatch({ type: "UPDATE_USER_ID", payload: response.data.unique_identifier });
            Cookies.set("indvestment_access_token", access_token, options);
            Cookies.set("indvestment_user_id", response.data.unique_identifier, options);
            localStorage.setItem("indvestment_refresh_token", response.data.token_info.refresh_token);
            navigate("/");
        } else {
            toast.dismiss();
            toast.error(response.message);
            setOtpValue("");
            setLoading(false);
        }
    };

    return (
        <section className='min-vh-100 mb-5 pb-5'>
            <div className='container'>
                <div className='row pt-3'>
                    <div className='offset-lg-1 col-lg-10 col-12'>
                        <div className={`${style.e_auth_wrapper} border-radius-16 overflow-hidden`}>
                            <div className='row'>
                                <div className='col-lg-6 col-md-6 col-sm-12 col-12 pe-0 d-lg-block d-md-block d-sm-none d-none'>
                                    <div className='e-bg-lotion-dark e-border-1px-right-lavender-light h-470'>
                                        <img src={step === 1 ? LoginStep1Banner : LoginStep2Banner}
                                            draggable={false}
                                            className='w-100'
                                            alt='login' />
                                    </div>
                                </div>

                                <div className='col-lg-6 col-md-6 col-sm-12 col-12 ps-lg-0 ps-md-0 '>
                                    {/* Phone input section */}
                                    {
                                        step === 1 ?
                                            <div className='padding-32px-lr padding-70px-top pb-4'>
                                                <div className='text-center mb-3'>
                                                    <h3 className='mb-0 e-primary-font-clash-display e-text-primary-color e-font-18 e-font-weight-500 line-height-24px'>
                                                        Get Started
                                                    </h3>
                                                    <p className='mb-0 e-alt-font-lato e-text-outer-space e-font-14 e-font-weight-400 line-height-20px'>
                                                        Enter your mobile number to receive an OTP
                                                    </p>
                                                </div>

                                                {/* Phone number */}
                                                <CustomPhoneInput label="Phone number"
                                                    placeholder="Enter your phone number"
                                                    type="text"
                                                    require
                                                    autoFocus={true}
                                                    value={number}
                                                    error={numberError}
                                                    className="w-100"
                                                    onKeyPress={_handleGetOtp}
                                                    handleChange={(e) => {
                                                        if (e.length <= 10) {
                                                            setNumber(e);
                                                        }
                                                    }} />

                                                {/* Get otp button */}
                                                <PrimaryButton name="Get OTP"
                                                    className="mt-4 w-100 h-48"
                                                    status={loading}
                                                    disabled={loading}
                                                    onPress={_handleGetOtp} />
                                                <p className='mb-0 padding-12px-top e-font-14 e-alt-font-lato line-height-20px e-text-chinese-black e-font-weight-400'>
                                                    New?
                                                    <span className='ps-1 e-text-primary-color e-font-weight-600 cursor-pointer text-decoration-hover-underline'
                                                        onClick={_handleSignUp}>
                                                        Create your account now!
                                                    </span>
                                                </p>
                                            </div>
                                            :
                                            // OTP section
                                            <div className='padding-32px-lr py-4'>
                                                <Icon icon={"back-icon"}
                                                    size="28px"
                                                    className='cursor-pointer'
                                                    onClick={_handleBack} />

                                                <div className='text-center pb-3 pt-4'>
                                                    <h3 className='mb-0 e-primary-font-clash-display e-text-primary-color e-font-20 e-font-weight-500 line-height-24px'>
                                                        Verify OTP
                                                    </h3>
                                                    <p className='px-lg-5 px-3 mb-0 e-alt-font-lato e-text-outer-space e-font-14 e-font-weight-400 line-height-20px'>
                                                        Enter the One-Time Password (OTP) sent to your registered mobile number <span className='d-inline-flex'>+91-{number.replace(/(\d{2})\d{5}(\d{3})/, '$1*****$2')}</span>
                                                    </p>
                                                </div>

                                                {/* Enter OTP input */}
                                                <OTPInput label="Enter OTP"
                                                    require
                                                    ref={inputRef}
                                                    value={otpValue}
                                                    spellCheck="false"
                                                    error={otpValueError}
                                                    focused_input={focusedInput}
                                                    onFocus={() => setFocusedInput(2)}
                                                    onBlur={() => setFocusedInput(0)}
                                                    onKeyPress={_handleVerifyOtp}
                                                    onChange={_handleChangeOTP} />

                                                <p className='d-flex flex-wrap gap-1 mb-3 mt-4 e-font-14 e-alt-font-lato line-height-20px e-text-chinese-black e-font-weight-400'>
                                                    Didn’t receive code yet?
                                                    <span className={`${disableResend || otpTimer !== 0 ? "pe-none opacity-75" : ""} text-nowrap e-text-primary-color e-font-weight-600 cursor-pointer text-decoration-hover-underline`}
                                                        onClick={_handleResendOtp}>
                                                        Resend OTP
                                                        {
                                                            otpTimer !== 0 && ` in ${otpTimer} ${otpTimer > 1 ? "seconds" : "second"}`
                                                        }
                                                    </span>
                                                </p>

                                                {/* Verify otp */}
                                                <PrimaryButton name="Continue"
                                                    status={loading}
                                                    disabled={loading}
                                                    className="w-100 h-48"
                                                    onPress={_handleVerifyOtp} />
                                            </div>


                                        // Pin section
                                        // <div className='padding-32px-lr py-4'>
                                        //     <Icon icon={"back-icon"}
                                        //         size="28px"
                                        //         className='cursor-pointer'
                                        //         onClick={() => _handleBack("otp")} />

                                        //     <div className='text-center pb-3 pt-4'>
                                        //         <h3 className='mb-0 e-primary-font-clash-display e-text-primary-color e-font-20 e-font-weight-500 line-height-24px'>
                                        //             Welcome Aboard!
                                        //         </h3>
                                        //         <p className='px-lg-5 px-3 mb-0 e-alt-font-lato e-text-outer-space e-font-14 e-font-weight-400 line-height-20px'>
                                        //             Enter your 4-digit MPIN to log in securely.
                                        //         </p>
                                        //     </div>

                                        //     {/* Enter pin input */}
                                        //     <OTPInput label="Enter 4-digit MPIN"
                                        //         require
                                        //         ref={inputRef}
                                        //         value={pin}
                                        //         spellCheck="false"
                                        //         error={pinError}
                                        //         focused_input={focusedInputPin}
                                        //         onFocus={() => setFocusedInputPin(2)}
                                        //         onBlur={() => setFocusedInputPin(0)}
                                        //         onKeyPress={_handleLogin}
                                        //         onChange={_handleChangePIN} />

                                        //     <p className='mb-0 mt-4 e-font-14 e-alt-font-lato line-height-20px  e-text-primary-color e-font-weight-600 cursor-pointer text-decoration-hover-underline w-max-content'>
                                        //         Forgot MPIN?
                                        //     </p>

                                        //     {/* Verify otp */}
                                        //     <PrimaryButton name="Login"
                                        //         className="margin-12px-top w-100"
                                        //         onPress={_handleLogin} />
                                        // </div>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section >
    )
}

export default Login;
